import { useTranslation } from "react-i18next";
import "./ChangePassword.scss";
import { AssessmentButton, InputField, showToast } from "ui-components";
import { Field, Form, Formik } from "formik";
import { loginUserData } from "../../utils/Common/Constant";
import * as Yup from "yup";
import { changePassword } from "../../service/Scope/Scope";
import i18n from "../../i18nConfig";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
  const { t } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const loginUserSchema = Yup.object().shape({
    oldPassword: Yup.string()
      .min(6, t("ERROR.PASSWORD_MUST"))
      .max(32, t("ERROR.PASSWORD_MUST"))
      .matches(/[a-zA-Z]/, t("ERROR.PASSWORD_LETTER"))
      .matches(/[0-9]/, t("ERROR.PASSWORD_NUMBER"))
      .matches(
        /[!@#$%^&*(),.?":{}\\|<>`~\-_=+[\];:'"<>/?]/,
        t("ERROR.PASSWORD_SPECIAL"),
      )

      .trim()
      .required(t("ERROR.OLD_PASSWORD_REQUIRED")),
    password: Yup.string()
      .min(6, t("ERROR.PASSWORD_MUST"))
      .max(32, t("ERROR.PASSWORD_MUST"))
      .matches(/[a-zA-Z]/, t("ERROR.PASSWORD_LETTER"))
      .matches(/[0-9]/, t("ERROR.PASSWORD_NUMBER"))
      .matches(
        /[!@#$%^&*(),.?":{}\\|<>`~\-_=+[\];:'"<>/?]/,
        t("ERROR.PASSWORD_SPECIAL"),
      )

      .trim()
      .required(t("ERROR.PASSWORD_REQUIRED")),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password"), undefined],
        t("ERROR.CONFIRM_PASSWORD_MATCH"),
      )
      .trim()
      .required(t("ERROR.CONFIRM_PASSWORD_REQUIRED")),
  });

  const changePasswordApi = async (
    data: any,
    resetForm: any,
    setSubmitting: any,
  ) => {
    try {
      const res = await changePassword(data);
      if (res?.status === 200) {
        showToast(res?.customMessage, "success");
        resetForm();
        setSubmitting(false);
        navigate(`/${currentLanguage}/assessment/list`, { replace: true });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={"container-fluid changePassContainer"}>
      <Formik
        initialValues={loginUserData}
        validationSchema={loginUserSchema}
        onSubmit={(value, { resetForm, setSubmitting }) => {
          changePasswordApi(
            {
              oldPassword: value?.oldPassword,
              newPassword: value?.password,
              type: "Company",
            },
            resetForm,
            setSubmitting,
          );
        }}
      >
        {({ errors, touched, values, setFieldValue, isSubmitting }) => {
          return (
            <div className="formContainer row">
              <div className="col-sm-10 formInstructionSec">
                <div className="formHeading">
                  <span>{t("GENERAL.CHANGE_YOUR_PASS")}</span>
                </div>
                <Form>
                  <div className="userSec">
                    <div className="userContent">
                      <Field name="oldPassword">
                        {() => (
                          <div className="passwordSec">
                            <div className="changePasswordLabels">
                              <label>{t("GENERAL.OLD_PASSWORD")} *</label>
                            </div>
                            <InputField
                              onChange={(newValue) =>
                                setFieldValue("oldPassword", newValue)
                              }
                              value={values.oldPassword}
                              additionalProps={{
                                className: "newPasswordInput",
                              }}
                              type="password"
                            />
                          </div>
                        )}
                      </Field>
                      {errors?.oldPassword && touched?.oldPassword ? (
                        <div className="errorMessage ">
                          {errors?.oldPassword}
                        </div>
                      ) : null}
                      <Field name="password">
                        {() => (
                          <div className="passwordSec">
                            <div className="changePasswordLabels">
                              <label>{t("GENERAL.NEW_PASSWORD")} *</label>
                            </div>
                            <InputField
                              onChange={(newValue) =>
                                setFieldValue("password", newValue)
                              }
                              value={values.password}
                              additionalProps={{
                                className: "newPasswordInput",
                              }}
                              type="password"
                            />
                          </div>
                        )}
                      </Field>
                      {errors?.password && touched?.password ? (
                        <div className="errorMessage ">{errors?.password}</div>
                      ) : null}
                      <Field name="confirmPassword">
                        {() => (
                          <div className="passwordSec">
                            <div className="changePasswordLabels">
                              <label>{t("GENERAL.CONFIRM_PASSWORD")} *</label>
                            </div>
                            <InputField
                              onChange={(newValue) =>
                                setFieldValue("confirmPassword", newValue)
                              }
                              value={values.confirmPassword}
                              additionalProps={{
                                className: "newPasswordInput",
                              }}
                              type="password"
                            />
                          </div>
                        )}
                      </Field>
                      {errors?.confirmPassword && touched?.confirmPassword ? (
                        <div className="errorMessage ">
                          {errors?.confirmPassword}
                        </div>
                      ) : null}
                      <AssessmentButton
                        className="submitBtn submitBtnCreatePassword"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? t("GENERAL.LOADING")
                          : t("GENERAL.SUBMIT")}
                      </AssessmentButton>
                    </div>
                  </div>
                </Form>
                <div className="passwordInstructions">
                  <div>
                    <li>{t("GENERAL.INSTRUCTION1")}</li>
                    <li>{t("GENERAL.INSTRUCTION2")}</li>
                    <li>{t("GENERAL.INSTRUCTION3")}</li>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChangePassword;
